import { OrderAttributes, OrderDetailAttributes } from "@albatrosdeveloper/ave-models-package";
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from "@angular/animations";
import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from "@angular/core";
import { ReactiveFormsModule, FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatOptionModule } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatMenuModule } from "@angular/material/menu";
import { MatPaginatorModule, PageEvent } from "@angular/material/paginator";
import { MatRadioModule } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";
import { MatSidenav, MatSidenavContainer } from "@angular/material/sidenav";
import { MatSlideToggle } from "@angular/material/slide-toggle";
import { MatSortModule } from "@angular/material/sort";
import { MatTableModule, MatTableDataSource } from "@angular/material/table";
import { MatTabsModule } from "@angular/material/tabs";
import { MatTooltipModule } from "@angular/material/tooltip";
import { NgSelectModule } from "@ng-select/ng-select";
import { WeightFormatPipe } from "app/pipes/weight-format.pipe";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { MaterialHistoryComponent } from "../material-history/material-history.component";
import { BatchesDetailComponent } from "app/modules/shared/batches-detail/batches-detail.component";
import { SummaryMaterialExport } from "../../exports/summary-material.export";
import { ExportFileFormat, ExportRange } from "app/layout/export-overlay";
import { DecimalFormatPipe } from "app/pipes/decimal-company.pipe";
import { BehaviorSubject } from "rxjs";

// Smart Grid
import { SmartGridComponent, ColDef, GridConfig } from 'app/shared/components/smart-grid';
import { DecimalCellRendererComponent } from 'app/shared/components/smart-grid';

// Cell Renderers
import { MaterialCellRendererComponent } from './cell-renderers/material-cell-renderer.component';
import { QuantityCellRendererComponent, QuantityCellRendererParams } from './cell-renderers/quantity-cell-renderer.component';
import { ActionsCellRendererComponent, ActionsCellRendererParams } from './cell-renderers/actions-cell-renderer.component';
import { DocumentTypeEnum } from "app/common/enums/document-types.enum";
import { UnitMeasureDisplayComponent } from "app/shared/components/unit-measure-display/unit-measure-display.component";

@Component({
  selector: "app-summary-material",
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatDialogModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSortModule,
    MatPaginatorModule,
    MatIconModule,
    MatSelectModule,
    MatOptionModule,
    MatTooltipModule,
    MatExpansionModule,
    MatCheckboxModule,
    MatMenuModule,
    NgSelectModule,
    MatDatepickerModule,
    FormsModule,
    MatTabsModule,
    NgxMatSelectSearchModule,
    MatRadioModule,
    DecimalFormatPipe,
    UnitMeasureDisplayComponent,
    SmartGridComponent,
  ],
  providers: [SummaryMaterialExport],
  templateUrl: "./summary-material.component.html",
  styleUrl: "./summary-material.component.scss",
  animations: [
    trigger("expandCollapse", [
      state(
        "void",
        style({
          height: "0px",
          opacity: 0,
        })
      ),
      state(
        "*",
        style({
          height: "*",
          opacity: 1,
        })
      ),
      transition("void <=> *", animate("300ms ease-in-out")),
      transition("* => void", animate("300ms ease-in-out")),
    ]),
  ],
})
export class SummaryMaterialComponent implements OnInit, OnDestroy {
  @Input() summaryData: any[] | null = null;
  @Input() UMPActive: boolean = false;
  @Input() isAllColumns: boolean = true;
  @Input() totalRecords: number = 0;
  @Input() type: DocumentTypeEnum | null = null;

  @ViewChild("summaryMaterialModal") summaryMaterialModal!: TemplateRef<any>;
  @ViewChild(SmartGridComponent) smartGrid!: SmartGridComponent<any>;

  @Output() refreshMonitor = new EventEmitter<void>();
  @Output() onSortChange: EventEmitter<{
    active: string;
    direction: "ASC" | "DESC";
  }> = new EventEmitter();

  // Observable for UMPActive - reactive approach
  private UMPActiveSubject$ = new BehaviorSubject<boolean>(false);
  public UMPActive$ = this.UMPActiveSubject$.asObservable();

  displayedColumns: string[] = [
    "view",
    "material",
    "price",
    "soldQuantity",
    //"baseQuantity",
    "stockConfirmedERP",
  ];
  footerColumns: string[] = ["footerLabel", "footerTotals"];
  dataSource = new MatTableDataSource<OrderDetailAttributes>([]);
  totalItems: number = 0;
  pageSize: number = 50;
  pageIndex: number = 1;

  // Smart Grid Configuration
  columnDefs: ColDef<any>[] = [];
  gridConfig: GridConfig<any> = {
    rowIdField: "_id",
    pagination: {
      enabled: false,
    },
    sorting: {
      enabled: true,
      mode: "local",
      multiColumn: false,
    },
    selection: {
      enabled: false,
    },
    search: {
      enabled: false,
    },
    columnFilters: {
      enabled: true,
      mode: "local",
      showFilterIcon: true,
      trigger: "click",
    },
    rowExpansion: {
      enabled: false,
    },
    styling: {
      density: "comfortable",
      maxHeight: "none",  // Sin restricción - expansión completa
      cellPaddingY: "0px",
    },
    responsive: {
      enabled: true,
    },
    settingsPanel: {
      enabled: false,
      position: "right",
      enableColumnVisibility: true,
      enableDensity: false,
      enableExport: true,
      defaultDensity: "comfortable",
    },
  };

  constructor(
    private dialog: MatDialog,
    private summaryMaterialExport: SummaryMaterialExport
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.summaryData && changes.summaryData.currentValue) {
      this.updateDataSource();
    }

    if (changes.UMPActive) {
      // Update Observable - this will automatically propagate to all subscribers
      this.UMPActiveSubject$.next(this.UMPActive);
    }

    // Initialize or reinitialize column definitions when key inputs change
    if (changes.isAllColumns || changes.UMPActive || changes.type) {
      this.updateDisplayedColumns();
      this.initializeColumnDefs();
    }

    // ALSO initialize on first data load (ensures columns exist when data arrives)
    if (changes.summaryData && changes.summaryData.firstChange && this.columnDefs.length === 0) {
      this.initializeColumnDefs();
    }
  }

  ngOnInit(): void {
    this.updateDataSource();
    this.updateDisplayedColumns();
    this.initializeColumnDefs();
  }

  private updateDataSource(): void {
    if (this.summaryData) {
      this.dataSource.data = this.summaryData;
    }
  }
  private updateDisplayedColumns(): void {
    this.displayedColumns = this.isAllColumns
      ? ["view", "material", "price", "soldQuantity", "stockConfirmedERP"]
      : ["view", "material", "soldQuantity"];
  }

  ngOnDestroy(): void {}

  openHistory(element) {
    const dialogRef = this.dialog.open(MaterialHistoryComponent, {
      width: "90%",
      data: { element: element, UMPActive: this.UMPActive },
      disableClose: true,
    });

    dialogRef.componentInstance.refreshMonitor.subscribe(() => {
      this.refreshMonitor.emit();
      this.changeSort({ active: "creationDateTime", direction: "asc" });
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === undefined || result == "Close") {
      } else {
      }
    });
  }

  changeSort(event: { active: string; direction: "asc" | "desc" }) {
    if (this.dataSource.data.length === 0) return;
    const direction = event.direction === "asc" ? "ASC" : "DESC";
    this.onSortChange.emit({
      active: this.getSortField(event.active),
      direction,
    });
  }

  private getSortField(sortActive: string): keyof OrderAttributes {
    const sortFieldMapping: Record<string, keyof OrderAttributes> = {
      serial: "serial",
      soldTo: "soldTo",
      shipTo: <any>"shipTo",
      deliveryDateTime: <any>"deliveryDateTime",
      weight: <any>"weight",
    };
    return sortFieldMapping[sortActive] || "deliveryDateTime";
  }

  openBatchesDetail(order: any) {
    const batches = order.orders
      .filter((item) => item.detail?.batches?.length > 0)
      .map((item) => ({
        material: item.detail.material,
        batches: item.detail.batches,
      }));
    const detail = {
      material: order.orders[0].detail.material
        ? order.orders[0].detail.material
        : order.orders[0].detail[0].material,
      batches: batches.flatMap((b) => b.batches),
    };

    const dialogRef = this.dialog.open(BatchesDetailComponent, {
      width: "90%",
      data: {
        order: order,
        data: detail,
        type: "normal",
        isEdit: false,
        isChangeCostBatch: false,
      },
      disableClose: true,
    });
  }

  // Calcula el colspan si isAllColumns está activo o no
  getFooterLabelColspan(): number {
    return this.isAllColumns ? 4 : 2;
  }

  onExportButtonPressed() {
    if (!this.summaryMaterialExport.canStartTask()) return;
    this.summaryMaterialExport.export(
      ExportFileFormat.Excel,
      ExportRange.AllPages,
      this.totalItems,
      this.pageSize,
      this.pageIndex,
      this.type
    );
  }

  getTotalBaseQuantity(): number {
    if (!this.summaryData || this.summaryData.length === 0) {
      return 0;
    }
    return this.summaryData.reduce((total, element) => {
      const baseQuantity = element?.baseQuantity || 0;
      return total + baseQuantity;
    }, 0);
  }

  getTotalPararellQuantity(): number {
    if (!this.UMPActive || !this.summaryData || this.summaryData.length === 0) {
      return 0;
    }
    return this.summaryData.reduce((total, element) => {
      const pararellQuantity = element?.pararellQuantity || 0;
      return total + pararellQuantity;
    }, 0);
  }

  getCommonBaseUnit(): string | null {
    if (!this.summaryData || this.summaryData.length === 0) {
      return null;
    }
    const firstUnit = this.summaryData[0]?.baseUnitOfMeasure?.name;
    if (!firstUnit) return null;
    const allHaveSameUnit = this.summaryData.every(
      (item) => item?.baseUnitOfMeasure?.name === firstUnit
    );
    return allHaveSameUnit ? firstUnit : null;
  }

  getCommonParallelUnit(): string | null {
    if (!this.UMPActive || !this.summaryData || this.summaryData.length === 0) {
      return null;
    }
    const firstUnit = this.summaryData[0]?.pararellUnitOfMeasure?.name;
    if (!firstUnit) return null;
    const allHaveSameUnit = this.summaryData.every(
      (item) => item?.pararellUnitOfMeasure?.name === firstUnit
    );
    return allHaveSameUnit ? firstUnit : null;
  }

  // ============================================================================
  // SMART GRID METHODS
  // ============================================================================

  /**
   * Initialize column definitions for smart-grid
   */
  private initializeColumnDefs(): void {
    this.columnDefs = [
      // 1. Actions Column (pinned left)
      {
        colId: "actions",
        headerName: "",
        width: 70,
        minWidth: 70,
        maxWidth: 70,
        sortable: false,
        filterable: false,
        pinned: "left",
        lockPinned: true,
        cellRenderer: ActionsCellRendererComponent,
        cellRendererParams: {
          onOpenHistory: (data: any) => this.openHistory(data),
        } as ActionsCellRendererParams,
        cellClass: "text-center",
      },

      // 2. Material Column
      {
        field: "name" as any,
        colId: "material",
        headerName: "Material",
        flex: 2,
        minWidth: 200,
        sortable: true,
        filterable: true,
        cellRenderer: MaterialCellRendererComponent,
      },

      // 3. Price Column (conditional)
      ...(this.isAllColumns
        ? [
            {
              field: "price" as any,
              headerName: "Precio",
              width: 120,
              minWidth: 100,
              sortable: true,
              filterable: true,
              cellRenderer: DecimalCellRendererComponent,
              cellClass: "text-right",
              headerClass: "text-right",
            },
          ]
        : []),

      // 4. Quantity Column
      {
        field: "baseQuantity" as any,
        colId: "soldQuantity",
        headerName: this.isAllColumns ? "Cnt. Solicitada" : "Cantidad",
        flex: 3,
        minWidth: 250,
        sortable: true,
        filterable: true,
        cellRenderer: QuantityCellRendererComponent,
        cellRendererParams: {
          UMPActive$: this.UMPActive$, // Pass Observable instead of boolean
          type: this.type,
          onOpenBatchesDetail: (data: any) => this.openBatchesDetail(data),
        } as QuantityCellRendererParams,
      },

      // 5. Stock Confirmed ERP Column (conditional)
      ...(this.isAllColumns
        ? [
            {
              field: "stockConfirmedERP" as any,
              headerName: "Cnt. Confirmada",
              width: 140,
              minWidth: 120,
              sortable: true,
              filterable: true,
              valueFormatter: (value: any) => {
                return value ? (value ?? 0).toFixed(3) : "-";
              },
              cellClass: "text-right",
              headerClass: "text-right",
            },
          ]
        : []),
    ];
  }

}
